import { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";

/**
 * Every failure the app can show, whichever way it was raised: an RTK Query
 * rejection, an `HttpError` from one of the raw fetch helpers, a network
 * failure, an abort, or a plain exception.
 *
 * The API's error body is `{detail, code, ...extras}` (hg/api/errors.py):
 * `detail` is the sentence to show, `code` what to branch on.
 */
export type AppError = {
  /** HTTP status, or what happened instead of a response. */
  status: number | "network" | "aborted" | "client";
  /** The API's code (`quota.collections`, `auth.signed_out`, …), or a local one. */
  code: string;
  message: string;
  /** A rejected import document: one line per problem. */
  problems?: string[];
  retryAfter?: number;
  limit?: number;
  used?: number;
};

type ErrorBody = {
  detail?: unknown;
  code?: unknown;
  problems?: unknown;
  retry_after?: unknown;
  limit?: unknown;
  used?: unknown;
};

/** Thrown by the raw fetch helpers for a non-2xx response. */
export class HttpError extends Error {
  constructor(
    public status: number,
    public body: ErrorBody | null,
  ) {
    super(fromBody(status, body, defaultMessage(status)).message);
    this.name = "HttpError";
  }

  get code(): string {
    return typeof this.body?.code === "string"
      ? this.body.code
      : `http.${this.status}`;
  }
}

let unauthorizedHandler: (() => void) | null = null;

/**
 * Registered by `common/auth.ts`, which owns the signed-in state. Kept as a
 * hook rather than an import so this module stays below the store in the
 * import graph.
 */
export function setUnauthorizedHandler(handler: () => void) {
  unauthorizedHandler = handler;
}

/** Every request path calls this on a 401, whatever it then does with it. */
export function reportUnauthorized() {
  unauthorizedHandler?.();
}

/**
 * For the raw `fetch` helpers: returns the response if it is 2xx, otherwise
 * throws an `HttpError` carrying the API's error body (or none, when the
 * body is not JSON -- a proxy's HTML 502, say).
 */
export async function throwIfNotOk(response: Response): Promise<Response> {
  if (response.ok) return response;
  let body: ErrorBody | null = null;
  try {
    const parsed: unknown = await response.json();
    if (parsed && typeof parsed === "object") body = parsed as ErrorBody;
  } catch {
    // not JSON -- the status alone has to do
  }
  if (response.status === 401) reportUnauthorized();
  throw new HttpError(response.status, body);
}

export function defaultMessage(status: AppError["status"]): string {
  if (status === "network")
    return "Can't reach the server. Check your connection and try again.";
  if (status === "aborted") return "The request was cancelled.";
  if (status === "client") return "Something went wrong.";
  if (status === 401) return "You're signed out. Sign in to continue.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "It no longer exists, or you can't see it.";
  if (status === 413) return "That is too large to upload.";
  if (status === 429) return "Too many attempts. Wait a moment and try again.";
  if (status >= 500)
    return "Something went wrong on our side. Please try again.";
  return "The request could not be completed.";
}

function fromBody(
  status: number,
  body: ErrorBody | null,
  fallback: string,
): AppError {
  const error: AppError = {
    status,
    code: `http.${status}`,
    message: fallback,
  };
  if (!body) return error;
  if (typeof body.code === "string") error.code = body.code;
  if (typeof body.detail === "string" && body.detail) {
    error.message = body.detail;
  } else if (Array.isArray(body.detail)) {
    // An older server's import 422, or FastAPI's own validation shape.
    error.problems = body.detail.map(describe);
    if (error.problems.length === 1) error.message = error.problems[0];
  }
  if (Array.isArray(body.problems)) error.problems = body.problems.map(String);
  if (typeof body.retry_after === "number") error.retryAfter = body.retry_after;
  if (typeof body.limit === "number") error.limit = body.limit;
  if (typeof body.used === "number") error.used = body.used;
  return error;
}

function describe(item: unknown): string {
  if (item && typeof item === "object" && "msg" in item)
    return String((item as { msg: unknown }).msg);
  return String(item);
}

const FETCH_FAILED = /fetch|network|load failed/i;

function isFetchBaseQueryError(e: object): e is FetchBaseQueryError {
  return "status" in e && ("data" in e || "error" in e);
}

/**
 * Turns anything a `catch` can receive into an `AppError`. `fallback` replaces
 * the generic per-status message, never a message the server wrote.
 */
export function parseError(e: unknown, fallback?: string): AppError {
  if (e instanceof HttpError) {
    return fromBody(e.status, e.body, fallback ?? defaultMessage(e.status));
  }
  if (e instanceof DOMException && e.name === "AbortError") {
    return {
      status: "aborted",
      code: "aborted",
      message: defaultMessage("aborted"),
    };
  }
  if (e && typeof e === "object" && isFetchBaseQueryError(e)) {
    const { status } = e;
    if (typeof status === "number") {
      return fromBody(
        status,
        (e.data && typeof e.data === "object" ? e.data : null) as ErrorBody,
        fallback ?? defaultMessage(status),
      );
    }
    if (status === "PARSING_ERROR") {
      // A response came back, just not JSON: treat it by its real status.
      return {
        status: e.originalStatus,
        code: `http.${e.originalStatus}`,
        message: fallback ?? defaultMessage(e.originalStatus),
      };
    }
    // FETCH_ERROR / TIMEOUT_ERROR: no response at all.
    return {
      status: "network",
      code: "network",
      message: defaultMessage("network"),
    };
  }
  if (e instanceof TypeError && FETCH_FAILED.test(e.message)) {
    // What `fetch` itself throws when the request never gets a response --
    // worded differently by every browser, and only told apart from a
    // TypeError in our own code by that wording.
    return {
      status: "network",
      code: "network",
      message: defaultMessage("network"),
    };
  }
  return {
    status: "client",
    code: "client",
    message:
      fallback ??
      (e instanceof Error && e.message ? e.message : defaultMessage("client")),
  };
}

/** True for anything that came from talking to the API. */
export function isApiFailure(e: unknown): boolean {
  if (e instanceof HttpError) return true;
  return !!e && typeof e === "object" && isFetchBaseQueryError(e);
}

/**
 * The message to show for an RTK Query mutation's rejected value (as thrown
 * by `.unwrap()`) or any other failure. `fallback` is used only when the
 * server did not say anything itself.
 */
export function getApiErrorDetail(e: unknown, fallback: string): string {
  return parseError(e, fallback).message;
}
